import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import Chat from "@/components/Chat";
import {
  Heart, Phone, ShieldCheck, Activity, Loader2, CreditCard, Smartphone,
  CheckCircle2, Siren, UserPlus, HeartPulse, Clock, PhoneCall, Sparkles, MessageSquare, MapPin,
} from "lucide-react";

const fmtTime = (iso) => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const eventIcon = { check_in: Clock, check_out: Clock, task: CheckCircle2, vitals: Activity, note: Heart, sos: Siren };

function PaymentView({ sub, payment, onPaid }) {
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (!ref.trim()) { toast.error("Enter the UPI reference / UTR"); return; }
    setBusy(true);
    try { await api.post(`/eldercare/subscriptions/${sub.id}/confirm-payment`, { reference: ref }); toast.success("Subscription activated"); onPaid(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm"); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-1"><CreditCard className="w-5 h-5 text-[#D97706]" /><h3 className="font-head text-xl font-semibold text-[#041E26]">Pay ₹{payment.amount}/mo to activate</h3></div>
      <p className="text-sm text-slate-500 mb-5">{sub.package_name} plan for {sub.elder_name}</p>
      {payment.configured && payment.qr && (
        <div className="grid sm:grid-cols-2 gap-5 items-center">
          <div className="bg-white border border-slate-200 rounded-2xl p-4 text-center"><img src={payment.qr} alt="UPI QR" data-testid="veda-upi-qr" className="w-44 h-44 mx-auto" /><p className="text-xs text-slate-400 mt-2">Scan with any UPI app</p></div>
          <div>
            <div className="text-sm"><span className="text-slate-400">Pay to</span><div className="font-semibold text-[#041E26]">{payment.payee}</div><div className="font-mono-c text-sm">{payment.upi_id}</div></div>
            <a href={payment.link} className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#041E26] text-white text-sm font-semibold"><Smartphone className="w-4 h-4" /> Open UPI app</a>
          </div>
        </div>
      )}
      <Label className="text-xs font-semibold text-slate-600 mt-6 block">Enter UPI reference / UTR after paying</Label>
      <Input data-testid="veda-upi-ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. 4198XXXXXX12" className="mt-1.5 h-12 rounded-xl" />
      <Button data-testid="veda-confirm-pay" onClick={confirm} disabled={busy} className="w-full mt-5 h-12 rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-bold gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> I&apos;ve paid — activate</>}</Button>
      <p className="text-[11px] text-slate-400 mt-3 text-center">Manual verification (UPI). Swappable to Razorpay auto-confirm later.</p>
    </div>
  );
}

function EnrollForm({ onCreated }) {
  const [f, setF] = useState({ name: "", age: "", gender: "", city: "", address: "", conditions: "", emergency_contact: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.name || !f.age) { toast.error("Name and age are required"); return; }
    setBusy(true);
    try { const { data } = await api.post("/eldercare/elders", { ...f, age: Number(f.age) || 0 }); toast.success("Elder enrolled"); onCreated(data.elder); }
    catch { toast.error("Could not enroll"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-7 max-w-2xl">
      <h2 className="font-head text-2xl font-semibold text-[#041E26]">Enroll your loved one</h2>
      <p className="text-slate-500 mt-1 mb-6">Tell us about the elder you&apos;d like us to care for.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label className="text-xs font-semibold text-slate-600">Full name</Label><Input data-testid="elder-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
        <div><Label className="text-xs font-semibold text-slate-600">Age</Label><Input data-testid="elder-age" type="number" value={f.age} onChange={(e) => set("age", e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
        <div><Label className="text-xs font-semibold text-slate-600">City</Label><Input data-testid="elder-city" value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Delhi" className="mt-1.5 h-11 rounded-xl" /></div>
        <div><Label className="text-xs font-semibold text-slate-600">Emergency contact</Label><Input data-testid="elder-emergency" value={f.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} placeholder="+91…" className="mt-1.5 h-11 rounded-xl" /></div>
        <div className="md:col-span-2"><Label className="text-xs font-semibold text-slate-600">Address</Label><Input data-testid="elder-address" value={f.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
        <div className="md:col-span-2"><Label className="text-xs font-semibold text-slate-600">Health conditions</Label><Input data-testid="elder-conditions" value={f.conditions} onChange={(e) => set("conditions", e.target.value)} placeholder="e.g. Hypertension, diabetes" className="mt-1.5 h-11 rounded-xl" /></div>
      </div>
      <Button data-testid="enroll-elder-btn" onClick={submit} disabled={busy} className="mt-6 h-12 px-8 rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-bold gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4" /> Enroll & choose a plan</>}</Button>
    </div>
  );
}

function PlanPicker({ elder, packages, onSubscribed }) {
  const [pay, setPay] = useState(null);
  const subscribe = async (pkg) => {
    try { const { data } = await api.post("/eldercare/subscribe", { elder_id: elder.id, package_id: pkg.id }); setPay(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start subscription"); }
  };
  return (
    <div>
      <h2 className="font-head text-2xl font-semibold text-[#041E26] mb-1">Choose a plan for {elder.name}</h2>
      <p className="text-slate-500 mb-6">Monthly subscriptions. Cancel anytime.</p>
      <div className="grid md:grid-cols-3 gap-5">
        {packages.map((p) => (
          <div key={p.id} data-testid={`plan-${p.id}`} className={`rounded-2xl border p-6 flex flex-col ${p.highlight ? "border-[#F59E0B] bg-amber-50/40 shadow-lg" : "border-slate-200 bg-white"}`}>
            {p.highlight && <span className="self-start text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full bg-[#F59E0B] text-[#041E26] mb-2">Most popular</span>}
            <h3 className="font-head text-xl font-semibold text-[#041E26]">{p.name}</h3>
            <p className="text-sm text-slate-500">{p.tagline}</p>
            <div className="mt-3 font-head text-3xl font-semibold text-[#041E26]">₹{p.price_monthly.toLocaleString("en-IN")}<span className="text-sm text-slate-400 font-normal">/mo</span></div>
            <ul className="mt-4 space-y-2 flex-1">
              {p.features.map((ft) => <li key={ft} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />{ft}</li>)}
            </ul>
            <Button data-testid={`subscribe-${p.id}`} onClick={() => subscribe(p)} className={`mt-5 rounded-full font-semibold ${p.highlight ? "bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26]" : "bg-[#041E26] hover:bg-[#0A2F3C] text-white"}`}>Subscribe</Button>
          </div>
        ))}
      </div>
      {pay && (
        <Dialog open onOpenChange={() => setPay(null)}>
          <DialogContent data-testid="veda-payment-dialog" className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
            <PaymentView sub={pay.subscription} payment={pay.payment} onPaid={() => { setPay(null); onSubscribed(); }} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function FamilyDashboard({ data, reload }) {
  const { active, elder, caregivers, feed, latest_vitals, sos_active, next_visit } = data;
  const [masked, setMasked] = useState(null);
  const [sathiIdent, setSathiIdent] = useState("");
  const [assignment, setAssignment] = useState(null);
  const [showChat, setShowChat] = useState(false);

  useEffect(() => {
    if (elder) api.get(`/eldercare/sathi-assignment/${elder.id}`).then(({ data: d }) => setAssignment(d.assignment)).catch(() => {});
  }, [elder]);

  const assignSathi = async () => {
    if (!sathiIdent.trim()) { toast.error("Enter the Sathi's mobile or email"); return; }
    const isEmail = sathiIdent.includes("@");
    try {
      await api.post("/eldercare/assign-sathi", { elder_id: elder.id, identifier: sathiIdent, channel: isEmail ? "email" : "sms" });
      toast.success("Sathi assigned — they can now log in and care for " + elder.name);
      setSathiIdent(""); api.get(`/eldercare/sathi-assignment/${elder.id}`).then(({ data: d }) => setAssignment(d.assignment));
    } catch { toast.error("Could not assign"); }
  };

  const sos = async () => {
    try { await api.post(`/eldercare/subscriptions/${active.id}/sos`); toast.error("SOS dispatched — rapid response en route"); reload(); }
    catch { toast.error("Could not trigger SOS"); }
  };
  const resolveSos = async () => { try { await api.post(`/eldercare/subscriptions/${active.id}/resolve-sos`); reload(); } catch { /* ignore */ } };
  const call = async (cg) => { try { const { data: m } = await api.get(`/eldercare/masked-call/${cg.id}`); setMasked(m); } catch { toast.error("Could not connect"); } };

  return (
    <div className="space-y-6">
      {sos_active && (
        <div className="rounded-2xl bg-red-600 text-white p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3"><Siren className="w-6 h-6 animate-pulse" /><div><div className="font-semibold">SOS Active — rapid response dispatched</div><div className="text-white/80 text-sm">Our medical team is en route to {elder?.name}.</div></div></div>
          <Button data-testid="resolve-sos-btn" onClick={resolveSos} variant="outline" className="rounded-full bg-white text-red-600 border-white hover:bg-white/90">Mark resolved</Button>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Elder + plan */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-head text-xl font-semibold text-[#041E26]">{elder?.name}</h3>
              <p className="text-sm text-slate-500">{elder?.age} yrs · {elder?.city || "—"}</p>
            </div>
            <Badge className="bg-green-100 text-green-700">{active.package_name}</Badge>
          </div>
          {elder?.conditions && <p className="text-xs text-slate-500 mt-3">Conditions: {elder.conditions}</p>}
          <div className="mt-4 flex items-center gap-2 text-sm text-slate-600"><Clock className="w-4 h-4 text-[#D97706]" /> Next visit: {next_visit ? fmtTime(next_visit) : "—"}</div>
          <Button data-testid="sos-btn" onClick={sos} className="w-full mt-5 h-12 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold gap-2"><Siren className="w-5 h-5" /> Trigger SOS</Button>
        </div>

        {/* Vitals */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-head text-lg font-semibold text-[#041E26] flex items-center gap-2"><HeartPulse className="w-5 h-5 text-[#D97706]" /> Latest vitals</h3>
          {latest_vitals ? (
            <div className="grid grid-cols-2 gap-3 mt-4">
              {[["Blood pressure", latest_vitals.bp], ["Heart rate", latest_vitals.hr + " bpm"], ["SpO₂", latest_vitals.spo2 + "%"], ["Blood sugar", latest_vitals.sugar + " mg/dL"]].map(([k, v]) => (
                <div key={k} className="rounded-xl bg-slate-50 p-3"><div className="text-xs text-slate-400">{k}</div><div className="font-head text-lg font-semibold text-[#041E26]">{v}</div></div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 mt-4">No vitals recorded yet.</p>}
        </div>

        {/* Care team */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-head text-lg font-semibold text-[#041E26] mb-4">Your care team</h3>
          <div className="space-y-3">
            {caregivers.map((c) => (
              <div key={c.id} className="flex items-center gap-3">
                <img src={c.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-[#041E26] truncate">{c.name}</div><div className="text-xs text-slate-400">{c.title}</div></div>
                <button data-testid={`masked-call-${c.id}`} onClick={() => call(c)} className="w-9 h-9 rounded-full bg-[#041E26] text-white flex items-center justify-center hover:bg-[#0A2F3C]"><Phone className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sathi assignment + chat */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-head text-lg font-semibold text-[#041E26] flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#D97706]" /> Your Sathi (caregiver)</h3>
          {assignment ? (
            <div className="mt-3">
              <p className="text-sm text-slate-600">
                {assignment.sathi_name ? (
                  <><span className="font-semibold text-[#041E26]">{assignment.sathi_name}</span> · <span className="text-slate-500">{assignment.sathi_identifier}</span></>
                ) : (
                  <>Assigned to <span className="font-semibold text-[#041E26]">{assignment.sathi_identifier}</span></>
                )}
              </p>
              {(assignment.sathi_address_line || assignment.sathi_address_city) && (
                <p data-testid="sathi-address" className="text-xs text-slate-500 mt-1.5 flex items-start gap-1.5 leading-snug">
                  <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-[#B45309]" />
                  <span>{[assignment.sathi_address_line, assignment.sathi_address_city, assignment.sathi_address_state, assignment.sathi_address_pincode].filter(Boolean).join(", ")}</span>
                </p>
              )}
              <p className="text-xs text-slate-400 mt-1">They log in via the <b>Caregiver (Sathi)</b> tab with this number/email to care for {elder?.name}.</p>
              <Button data-testid="open-chat-btn" onClick={() => setShowChat((v) => !v)} className="mt-4 rounded-full bg-[#041E26] hover:bg-[#0A2F3C] text-white gap-2"><MessageSquare className="w-4 h-4" /> {showChat ? "Hide chat" : "Chat with Sathi"}</Button>
            </div>
          ) : (
            <div className="mt-3">
              <p className="text-sm text-slate-500 mb-3">Add your caregiver&apos;s mobile number or email — they&apos;ll get access to {elder?.name}&apos;s daily care checklist.</p>
              <div className="flex gap-2">
                <Input data-testid="sathi-ident-input" value={sathiIdent} onChange={(e) => setSathiIdent(e.target.value)} placeholder="+91… or email" className="h-11 rounded-xl" />
                <Button data-testid="assign-sathi-btn" onClick={assignSathi} className="h-11 rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-semibold px-5">Assign</Button>
              </div>
            </div>
          )}
        </div>
        {showChat && elder && <Chat elderId={elder.id} meRole="family" />}
      </div>

      {/* Live feed */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="flame-dot w-2 h-2 rounded-full bg-green-500" />
          <h3 className="font-head text-lg font-semibold text-[#041E26]">Live care activity</h3>
          <span className="text-xs text-slate-400">· updates in real time</span>
        </div>
        <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
          <AnimatePresence initial={false}>
            {feed.map((e) => {
              const Icon = eventIcon[e.type] || Sparkles;
              return (
                <motion.div key={e.id} layout initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex items-start gap-3 p-3 rounded-xl ${e.type === "sos" ? "bg-red-50" : "bg-slate-50"}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${e.type === "sos" ? "bg-red-100 text-red-600" : "bg-amber-100 text-[#D97706]"}`}><Icon className="w-4 h-4" /></div>
                  <div className="flex-1">
                    <div className="text-sm text-[#041E26]">{e.message}</div>
                    {e.vitals && <div className="text-xs text-slate-500 mt-1">BP {e.vitals.bp} · HR {e.vitals.hr} · SpO₂ {e.vitals.spo2}% · Sugar {e.vitals.sugar}</div>}
                    <div className="text-xs text-slate-400 mt-0.5">{e.caregiver_name} · {fmtTime(e.created_at)}</div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {masked && (
        <Dialog open onOpenChange={() => setMasked(null)}>
          <DialogContent data-testid="masked-call-dialog" className="max-w-sm rounded-3xl text-center py-8">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4"><PhoneCall className="w-8 h-8 text-green-600" /></div>
            <h3 className="font-head text-xl font-semibold text-[#041E26]">Connecting to {masked.caregiver_name}</h3>
            <p className="font-mono-c text-2xl text-[#041E26] mt-3">{masked.masked_number}</p>
            <p className="text-xs text-slate-400 mt-3 px-4">{masked.note}</p>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

export default function FamilyPortal() {
  const [data, setData] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [d, p] = await Promise.all([api.get("/eldercare/dashboard"), api.get("/eldercare/packages")]);
    setData(d.data); setPackages(p.data.packages); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Live polling when a plan is active
  useEffect(() => {
    if (!data?.active) return;
    const t = setInterval(() => { api.get("/eldercare/dashboard").then(({ data: d }) => setData(d)).catch(() => {}); }, 6000);
    return () => clearInterval(t);
  }, [data?.active]);

  if (loading) return <div className="text-slate-400 py-10">Loading…</div>;

  const hasElder = data.elder || (data.subscriptions && data.subscriptions.length > 0);

  return (
    <div>
      {data.active ? (
        <FamilyDashboard data={data} reload={load} />
      ) : hasElder && data.subscriptions.some((s) => s.status === "pending_payment") ? (
        <PendingResume data={data} packages={packages} reload={load} />
      ) : (
        <ElderSetup packages={packages} reload={load} />
      )}
    </div>
  );
}

// Enroll (if no elder) then pick a plan
function ElderSetup({ packages, reload }) {
  const [elder, setElder] = useState(null);
  const [existing, setExisting] = useState([]);
  useEffect(() => { api.get("/eldercare/elders").then(({ data }) => { setExisting(data.elders); if (data.elders[0]) setElder(data.elders[0]); }).catch(() => {}); }, []);
  if (!elder && existing.length === 0) return <EnrollWrap onCreated={(e) => setElder(e)} reload={reload} />;
  if (!elder) return <EnrollWrap onCreated={(e) => setElder(e)} reload={reload} />;
  return <PlanPicker elder={elder} packages={packages} onSubscribed={reload} />;
}

function EnrollWrap({ onCreated }) {
  return <EnrollForm onCreated={onCreated} />;
}

function PendingResume({ data, packages, reload }) {
  const pending = data.subscriptions.find((s) => s.status === "pending_payment");
  const [pay, setPay] = useState(null);
  const resume = async () => {
    // re-fetch payment info by creating a fresh UPI (amount from plan)
    const pkg = packages.find((p) => p.id === pending.package_id);
    setPay({ subscription: pending, payment: { configured: true, amount: pending.price_monthly, payee: "Candle Healthcare", upi_id: "", link: "#", qr: null } });
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-7 max-w-xl">
      <h2 className="font-head text-2xl font-semibold text-[#041E26]">Complete your Maa Papa Care subscription</h2>
      <p className="text-slate-500 mt-1 mb-5">{pending.package_name} for {pending.elder_name} · ₹{pending.price_monthly}/mo — payment pending.</p>
      <Button data-testid="resume-pay-btn" onClick={resume} className="h-12 px-6 rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-bold gap-2"><CreditCard className="w-4 h-4" /> Complete payment</Button>
      {pay && (
        <Dialog open onOpenChange={() => setPay(null)}>
          <DialogContent className="max-w-md rounded-3xl">
            <PaymentView sub={pay.subscription} payment={pay.payment} onPaid={() => { setPay(null); reload(); }} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
